/**
 * ScanReviewPanel — Full scan result editor with PDF viewer for HIL review.
 *
 * Renders all accounts, statements, and transactions in expandable sections.
 * Everything is editable inline. The component owns the full accounts state
 * and sends the complete modified structure back on save.
 *
 * Props (from agent render_scan_data tool):
 *   accounts: Array<Account> — full scan result accounts array
 *   title: string — header
 *   pdfUrl/pdfBucket/pdfKey: PDF source
 *   onInteraction: (event) => void — callback for save, copy-to-chat, etc.
 */

import React, { useState, useCallback, useRef } from 'react';
import PDFSidePanel from '../ui/pdf-side-panel';
import TransactionTable from './TransactionTable';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';
import useGetSignedUrl from '../../api/signedurl';
import styles from './ScanReviewPanel.module.css';
import { formatCurrency } from '../../utils/formatters';

function ScanReviewPanel({ accounts: initialAccounts = [], title, pdfUrl, pdfBucket, pdfKey, documentName = null, onInteraction }) {
  const [accounts, setAccounts] = useState(initialAccounts);
  const [highlightedTxn, setHighlightedTxn] = useState(null);
  const [expandedAccounts, setExpandedAccounts] = useState(() => new Set(initialAccounts.map((_, i) => i)));
  const [expandedStatements, setExpandedStatements] = useState(() => {
    const set = new Set();
    initialAccounts.forEach((acct, ai) => {
      acct.statements?.forEach((_, si) => set.add(`${ai}-${si}`));
    });
    return set;
  });
  const [revealedAccounts, setRevealedAccounts] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { type, acctIdx, stmtIdx, label }
  const undoStackRef = useRef([]);

  // Pre-signed URL for PDF
  const { data: signedUrl } = useGetSignedUrl(pdfBucket, pdfKey, !!(pdfBucket && pdfKey && !pdfUrl));
  const effectivePdfUrl = pdfUrl || signedUrl;

  // Deep clone helper
  const cloneAccounts = useCallback((accts) => JSON.parse(JSON.stringify(accts)), []);

  // Push current state to undo stack before mutation
  const pushUndo = useCallback(() => {
    undoStackRef.current = [...undoStackRef.current, cloneAccounts(accounts)];
  }, [accounts, cloneAccounts]);

  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current.pop();
    setAccounts(prev);
    setDirty(undoStackRef.current.length > 0);
  }, []);

  // --- Account-level operations ---
  const updateAccountField = useCallback((acctIdx, field, value) => {
    pushUndo();
    setAccounts(prev => {
      const next = cloneAccounts(prev);
      next[acctIdx][field] = value;
      return next;
    });
    setDirty(true);
  }, [pushUndo, cloneAccounts]);

  const addAccount = useCallback(() => {
    pushUndo();
    setAccounts(prev => [...prev, {
      account_type: 'Checking',
      account_number: '',
      account_number_last4: '',
      bank_name: '',
      statements: [],
    }]);
    setExpandedAccounts(prev => new Set([...prev, accounts.length]));
    setDirty(true);
  }, [pushUndo, accounts.length]);

  const deleteAccount = useCallback((acctIdx) => {
    const acct = accounts[acctIdx];
    setPendingDelete({ type: 'account', acctIdx, label: `${acct.account_type || 'Account'} *${acct.account_number_last4 || '????'}` });
  }, [accounts]);

  const confirmPendingDelete = useCallback(() => {
    if (!pendingDelete) return;
    pushUndo();
    if (pendingDelete.type === 'account') {
      setAccounts(prev => prev.filter((_, i) => i !== pendingDelete.acctIdx));
    } else if (pendingDelete.type === 'statement') {
      setAccounts(prev => {
        const next = JSON.parse(JSON.stringify(prev));
        next[pendingDelete.acctIdx].statements.splice(pendingDelete.stmtIdx, 1);
        return next;
      });
    }
    setDirty(true);
    setPendingDelete(null);
  }, [pendingDelete, pushUndo]);

  // --- Statement-level operations ---
  const updateStatementField = useCallback((acctIdx, stmtIdx, field, value) => {
    pushUndo();
    setAccounts(prev => {
      const next = cloneAccounts(prev);
      next[acctIdx].statements[stmtIdx][field] = value;
      return next;
    });
    setDirty(true);
  }, [pushUndo, cloneAccounts]);

  const addStatement = useCallback((acctIdx) => {
    pushUndo();
    setAccounts(prev => {
      const next = cloneAccounts(prev);
      next[acctIdx].statements.push({
        begin_date: '',
        end_date: '',
        begin_balance: 0,
        end_balance: 0,
        bank_name: '',
        transactions: [],
      });
      return next;
    });
    const newStmtIdx = accounts[acctIdx].statements.length;
    setExpandedStatements(prev => new Set([...prev, `${acctIdx}-${newStmtIdx}`]));
    setDirty(true);
  }, [pushUndo, cloneAccounts, accounts]);

  const deleteStatement = useCallback((acctIdx, stmtIdx) => {
    const stmt = accounts[acctIdx].statements[stmtIdx];
    setPendingDelete({ type: 'statement', acctIdx, stmtIdx, label: `statement ${stmt.begin_date || ''} to ${stmt.end_date || ''}` });
  }, [accounts]);

  // --- Transaction-level: TransactionTable manages its own state, reports onChange ---
  const handleTransactionsChange = useCallback((acctIdx, stmtIdx, newTransactions) => {
    pushUndo();
    setAccounts(prev => {
      const next = cloneAccounts(prev);
      next[acctIdx].statements[stmtIdx].transactions = newTransactions;
      return next;
    });
    setDirty(true);
  }, [pushUndo, cloneAccounts]);

  // --- Save ---
  const handleSave = useCallback(() => {
    if (onInteraction) {
      setSaving(true);
      onInteraction({
        type: 'save_corrections',
        accounts: cloneAccounts(accounts),
      });
      // Reset dirty after a brief delay (API call happens async)
      setTimeout(() => {
        setDirty(false);
        setSaving(false);
        undoStackRef.current = [];
      }, 500);
    }
  }, [accounts, cloneAccounts, onInteraction]);

  // --- Interaction handler for child components ---
  const handleChildInteraction = useCallback((event) => {
    if (event.type === 'transaction_selected') {
      const txn = event.data;
      if (txn) {
        setHighlightedTxn({
          description: txn.description || '',
          amount: txn.amount,
          posting_date: txn.date || '',
          page_number: txn.page_number || 1,
          _key: Date.now(),
        });
      }
    }
    // Propagate copy_to_chat etc. to parent
    if (onInteraction && event.type !== 'save_corrections') {
      onInteraction(event);
    }
  }, [onInteraction]);

  // --- Toggle helpers ---
  const toggleAccount = (idx) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleStatement = (key) => {
    setExpandedStatements(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleRevealAccount = (idx) => {
    setRevealedAccounts(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  // Mask account number
  const maskAccountNumber = (num, last4) => {
    if (!num && !last4) return '****';
    if (!num) return `****${last4}`;
    return `${'*'.repeat(Math.max(0, num.length - 4))}${num.slice(-4)}`;
  };

  // --- Editable field inline component ---
  const EditableField = ({ value, onChange, label, type = 'text', className }) => {
    const [editing, setEditing] = useState(false);
    const [localVal, setLocalVal] = useState(value);

    if (editing) {
      return (
        <input
          autoFocus
          className={`${styles.inlineInput} ${className || ''}`}
          type={type}
          value={localVal}
          onChange={(e) => setLocalVal(e.target.value)}
          onBlur={() => {
            setEditing(false);
            const finalVal = type === 'number' ? parseFloat(localVal) || 0 : localVal;
            if (finalVal !== value) onChange(finalVal);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setLocalVal(value); setEditing(false); }
          }}
        />
      );
    }
    return (
      <span
        className={`${styles.editableValue} ${className || ''}`}
        onDoubleClick={() => { setLocalVal(value); setEditing(true); }}
        title="Double-click to edit"
      >
        {type === 'number' ? formatCurrency(value) : (value || '—')}
      </span>
    );
  };

  // --- Render ---
  const leftPanel = (
    <div className={styles.container}>
      {/* Top toolbar */}
      <div className={styles.topBar}>
        <h3 className={styles.title}>{title || 'Scan Result Editor'}</h3>
        <div className={styles.topActions}>
          <button className={styles.addBtn} onClick={addAccount}>+ Add Account</button>
          <button className={styles.undoBtn} onClick={handleUndo} disabled={undoStackRef.current.length === 0}>↩ Undo</button>
          {dirty && (
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save All Corrections'}
            </button>
          )}
        </div>
      </div>

      {/* Account sections */}
      <div className={styles.scrollArea}>
        {accounts.map((acct, acctIdx) => {
          const isExpanded = expandedAccounts.has(acctIdx);
          const isRevealed = revealedAccounts.has(acctIdx);
          const acctLabel = `${acct.account_type || 'Account'} ${isRevealed ? (acct.account_number || acct.account_number_last4 || '') : maskAccountNumber(acct.account_number, acct.account_number_last4)}`;

          return (
            <div key={acctIdx} className={styles.accountSection}>
              {/* Account header */}
              <div className={styles.accountHeader} onClick={() => toggleAccount(acctIdx)}>
                <span className={styles.expandIcon}>{isExpanded ? '▾' : '▸'}</span>
                <span className={styles.accountLabel}>{acctLabel}</span>
                {acct.bank_name && <span className={styles.bankName}>{acct.bank_name}</span>}
                <span className={styles.stmtCount}>{acct.statements?.length || 0} statement(s)</span>
              </div>

              {isExpanded && (
                <div className={styles.accountBody}>
                  {/* Account details (editable) */}
                  <div className={styles.accountDetails}>
                    <label>
                      Type: <EditableField value={acct.account_type || ''} onChange={(v) => updateAccountField(acctIdx, 'account_type', v)} />
                    </label>
                    <label>
                      Bank: <EditableField value={acct.bank_name || ''} onChange={(v) => updateAccountField(acctIdx, 'bank_name', v)} />
                    </label>
                    <label>
                      Account #:
                      <span className={styles.acctNumGroup}>
                        {isRevealed ? (
                          <EditableField
                            value={acct.account_number || ''}
                            onChange={(v) => {
                              updateAccountField(acctIdx, 'account_number', v);
                              if (v.length >= 4) updateAccountField(acctIdx, 'account_number_last4', v.slice(-4));
                            }}
                          />
                        ) : (
                          <span className={styles.maskedNum}>{maskAccountNumber(acct.account_number, acct.account_number_last4)}</span>
                        )}
                        <button
                          className={styles.revealBtn}
                          onClick={(e) => { e.stopPropagation(); toggleRevealAccount(acctIdx); }}
                          title={isRevealed ? 'Hide account number' : 'Show account number'}
                        >
                          {isRevealed ? '🔒' : '👁'}
                        </button>
                      </span>
                    </label>
                    <div className={styles.accountActions}>
                      <button className={styles.addBtn} onClick={() => addStatement(acctIdx)}>+ Add Statement</button>
                      <button className={styles.deleteBtn} onClick={() => deleteAccount(acctIdx)}>Delete Account</button>
                    </div>
                  </div>

                  {/* Statement sections */}
                  {acct.statements?.map((stmt, stmtIdx) => {
                    const stmtKey = `${acctIdx}-${stmtIdx}`;
                    const isStmtExpanded = expandedStatements.has(stmtKey);
                    const txnSum = (stmt.transactions || []).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                    const expectedChange = (stmt.end_balance || 0) - (stmt.begin_balance || 0);
                    const discrepancy = Math.round((txnSum - expectedChange) * 100) / 100;
                    const hasDiscrepancy = Math.abs(discrepancy) >= 0.01;

                    return (
                      <div key={stmtIdx} className={styles.statementSection}>
                        {/* Statement header */}
                        <div
                          className={`${styles.statementHeader} ${hasDiscrepancy ? styles.statementWarning : ''}`}
                          onClick={() => toggleStatement(stmtKey)}
                        >
                          <span className={styles.expandIcon}>{isStmtExpanded ? '▾' : '▸'}</span>
                          <span className={styles.stmtPeriod}>
                            {stmt.begin_date || '?'} — {stmt.end_date || '?'}
                          </span>
                          <span className={styles.stmtBal}>
                            {formatCurrency(stmt.begin_balance)} → {formatCurrency(stmt.end_balance)}
                          </span>
                          <span className={styles.txnCount}>
                            {stmt.transactions?.length || 0} txns
                          </span>
                          {hasDiscrepancy && (
                            <span className={styles.discrepancyBadge}>
                              Gap: {formatCurrency(discrepancy)}
                            </span>
                          )}
                        </div>

                        {isStmtExpanded && (
                          <div className={styles.statementBody}>
                            {/* Editable header fields */}
                            <div className={styles.stmtDetails}>
                              <label>
                                Begin: <EditableField value={stmt.begin_date || ''} onChange={(v) => updateStatementField(acctIdx, stmtIdx, 'begin_date', v)} />
                              </label>
                              <label>
                                End: <EditableField value={stmt.end_date || ''} onChange={(v) => updateStatementField(acctIdx, stmtIdx, 'end_date', v)} />
                              </label>
                              <label>
                                Begin bal: <EditableField value={stmt.begin_balance || 0} onChange={(v) => updateStatementField(acctIdx, stmtIdx, 'begin_balance', v)} type="number" />
                              </label>
                              <label>
                                End bal: <EditableField value={stmt.end_balance || 0} onChange={(v) => updateStatementField(acctIdx, stmtIdx, 'end_balance', v)} type="number" />
                              </label>
                              {stmt.bank_name !== undefined && (
                                <label>
                                  Bank: <EditableField value={stmt.bank_name || ''} onChange={(v) => updateStatementField(acctIdx, stmtIdx, 'bank_name', v)} />
                                </label>
                              )}
                              <button className={styles.deleteBtn} onClick={() => deleteStatement(acctIdx, stmtIdx)}>Delete Statement</button>
                            </div>

                            {/* Transaction table */}
                            <TransactionTable
                              transactions={stmt.transactions || []}
                              columns={['date', 'description', 'amount']}
                              beginBalance={stmt.begin_balance}
                              endBalance={stmt.end_balance}
                              onInteraction={handleChildInteraction}
                              onChange={(newTxns) => handleTransactionsChange(acctIdx, stmtIdx, newTxns)}
                              editable={true}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(!acct.statements || acct.statements.length === 0) && (
                    <div className={styles.emptyState}>No statements. Click "+ Add Statement" to create one.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {accounts.length === 0 && (
          <div className={styles.emptyState}>No accounts found in scan result. Click "+ Add Account" to create one.</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <PDFSidePanel
        leftChild={leftPanel}
        fileUrl={effectivePdfUrl}
        documentName={documentName}
        highlightedTxn={highlightedTxn}
      />
      <ConfirmBeforeActionDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.label || 'Item'}`}
        confirmMessage={`Are you sure you want to delete ${pendingDelete?.label || 'this item'}? This cannot be undone after saving.`}
        proceedButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        onProceed={confirmPendingDelete}
        onCancel={() => setPendingDelete(null)}
        list_element={() => null}
      />
    </>
  );
}

export default ScanReviewPanel;
